import { useLingui } from "@lingui/react/macro";
import Form from "@/components/Form/Form";
import InputField from "@medoma/components/InputField/InputField";
import { useFormContext, type FieldValues } from "react-hook-form";
import { addDays, isToday } from "date-fns";
import { useEffect } from "react";
import { PlainButton } from "@medoma/components/Button/Button";
import styles from "./TemplateDateInput.module.scss";
import { machineFormat } from "@medoma/models/date-and-time";
import { dateMonthAndYear } from "@/Utils/DateUtils";
import {
  useFieldPathWithPrefix,
  useFieldErrorWithPrefix,
} from "@/forms/FormFieldPathPrefixContext";

type IDateInputFields = { startDate: string; endDate: string };

type IQuickSelection = {
  label: string;
  date: Date;
};

type ITemplateDateInput = {
  hasEndDate?: boolean;
  minDate?: Date;
  quickSelections?: IQuickSelection[];
  size?: "default" | "compact";
};

/**
 * Essentially a copy of `DateInput` with two additions:
 * (1) prefix-aware field names via useFieldPathWithPrefix/useFieldErrorWithPrefix,
 *     so the same component can drive both a top-level startDate and a nested one
 *     (selectedActivities.<id>.startDate), and (2) a `size` prop for compact.
 * Everything else (end-date branch, quick selections, validation) is unchanged.
 * These could eventually be merged by teaching `DateInput` the same two things,
 * but that touches existing consumers in AddActivityForm, so kept separate here.
 */
export const TemplateDateInput = ({
  hasEndDate = false,
  minDate = new Date(),
  quickSelections,
  size = "default",
}: ITemplateDateInput) => {
  const {
    formState: { errors },
    register,
    setValue,
    unregister,
    watch,
  } = useFormContext<FieldValues>();

  const { t } = useLingui();

  const startDateFieldName =
    useFieldPathWithPrefix<IDateInputFields>("startDate");
  const endDateFieldName = useFieldPathWithPrefix<IDateInputFields>("endDate");
  const startDateError = useFieldErrorWithPrefix<IDateInputFields>(
    errors,
    "startDate",
  );
  const endDateError = useFieldErrorWithPrefix<IDateInputFields>(
    errors,
    "endDate",
  );

  const defaultQuickSelections = [
    { label: t`Idag`, date: new Date() },
    { label: t`Imorgon`, date: addDays(new Date(), 1) },
  ];

  const selections = quickSelections || defaultQuickSelections;
  const hasQuickSelections = selections && selections.length > 0;

  const minDateMessageString = isToday(minDate)
    ? t`idag`
    : dateMonthAndYear(minDate);

  const startDateOptions = {
    required: {
      value: true,
      message: hasEndDate ? t`Ange startdatum` : t`Ange datum`,
    },
    min: {
      value: machineFormat(minDate, "yyyy-MM-dd"),
      message: hasEndDate
        ? t`Startdatumet behöver vara ${minDateMessageString} eller senare`
        : t`Datumet behöver vara ${minDateMessageString} eller senare`,
    },
  };

  useEffect(() => {
    if (!hasEndDate) {
      unregister(endDateFieldName);
    }
  }, [hasEndDate, unregister, endDateFieldName]);

  const handleShortcutButtonClick = (date: Date) => {
    setValue(startDateFieldName, machineFormat(date, "yyyy-MM-dd"));
  };

  const renderShortcutButtons = () => (
    <div className={styles.shortcutButtons}>
      {selections.map((selection) => (
        <PlainButton
          key={selection.label}
          size="small"
          onClick={() => handleShortcutButtonClick(selection.date)}
        >
          {selection.label}
        </PlainButton>
      ))}
    </div>
  );

  return hasEndDate ? (
    <Form.Row type="flex">
      <div>
        <InputField
          label={t`Startdatum`}
          showOptionalLabel={false}
          errorMessage={startDateError}
          type="date"
          size={size}
          {...register(startDateFieldName, startDateOptions)}
        />
        {hasQuickSelections ? renderShortcutButtons() : null}
      </div>
      <InputField
        errorMessage={endDateError}
        key="endDate"
        label={t`Slutdatum`}
        showOptionalLabel
        type="date"
        size={size}
        min={watch(startDateFieldName)}
        {...register(endDateFieldName)}
      />
    </Form.Row>
  ) : (
    <div>
      <InputField
        label={t`Datum`}
        showOptionalLabel={false}
        errorMessage={startDateError}
        type="date"
        size={size}
        {...register(startDateFieldName, startDateOptions)}
      />
      {hasQuickSelections ? renderShortcutButtons() : null}
    </div>
  );
};
